import * as React from "react";

import axios, {
  AxiosError,
  AxiosRequestConfig,
  CancelTokenSource,
} from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { axiosApi } from "../interceptors/axios";
import { Result } from "../types/Result";

function useFetch<T = unknown>(url: string, options?: AxiosRequestConfig) {
  const [result, setResult] = useState<Result<T>>({
    data: null,
    errors: [],
    isSuccess: false,
    statusCode: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const cancelTokenSource = React.useRef<CancelTokenSource>();
  React.useEffect(() => {
    if (result.errors.length && typeof result.errors[0] === "string") {
      toast.error(result.errors[0]);
    }
  }, [result.errors]);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    cancelTokenSource.current = axios.CancelToken.source();

    try {
      const response = await axiosApi({
        ...options,
        url,
        cancelToken: cancelTokenSource.current.token,
      });
      setResult(response.data);
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("Request canceled:", err.message);
      } else {
        if (err instanceof AxiosError) {
          if (err.response?.data && Array.isArray(err.response?.data.errors))
            setResult(err.response.data);
        } else {
          setResult({
            data: null,
            isSuccess: false,
            errors: [err instanceof Error ? err.message : "An error occurred"],
            statusCode: "AxiosError",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [url, options]);

  useEffect(() => {
    fetchData();

    return () => {
      if (cancelTokenSource) {
        cancelTokenSource.current?.cancel(
          "Operation canceled due to new request."
        );
      }
    };
  }, [fetchData]);

  return { result, loading, reFetch: fetchData };
}

export default useFetch;
